import React, { useCallback, useMemo, useEffect, useRef, useContext } from 'react';
import ReactFlow, {
    applyNodeChanges,
    applyEdgeChanges,
    Controls,
    Background,
    useReactFlow,
    EdgeLabelRenderer,
    getBezierPath,
    getSmoothStepPath,
    Position,
    useStore,

} from 'reactflow';
import 'reactflow/dist/style.css';

import SituationNode from './SituationNode';
import ErrorOverlay from './ErrorOverlay';
import styles from './styles/Canvas.module.css';
import { useCanvas } from '../../../context/CanvasContext';
import { useEditorUI } from '../../../context/EditorUIContext';
import Edge, { getEllipsizedText } from './Edge';
import labelStyles from './styles/EdgeLabel.module.css';
import AuthContext from '../../../context/AuthContext';
import { hashStringToColor } from '../../../utils/collabColors';
import { throttle } from "lodash";
import { TUTORIAL_TARGETS, tutorialAttr, useTutorial, useTutorialLock } from '../../../tutorials';
import { KeyHighlightContext, useKeyHighlight } from './keys/KeyHighlightContext';
import { KeyRoleIcon } from './keys/KeyBadge';
import { KEY_VISUALS } from './keys/keyVisuals';
import { edgeKeyRoles, highlightSets } from './keys/keyGraph';

/**
 * How far out the canvas sits when a scenario opens with nothing on it yet.
 * Lower is further out. This is the one number to change to alter the starting
 * zoom of a brand-new scenario.
 */
const EMPTY_CANVAS_ZOOM = 0.7;
const EMPTY_CANVAS_VIEWPORT = { x: 0, y: 0, zoom: EMPTY_CANVAS_ZOOM };

/**
 * Applied to the one automatic fit that happens when a scenario that already
 * has situations is opened. `maxZoom` is the important part: without it a draft
 * of two or three nodes opens magnified to 200%, which is where the editor's
 * "far too zoomed in" first impression came from.
 */
const FIT_VIEW_OPTIONS = { padding: 0.25, maxZoom: 1 };

/** Roughly a situation's footprint, for turning a wanted centre into a top-left. */
const NODE_SIZE = { width: 160, height: 60 };
const topLeftOf = ({ x, y }) => ({ x: x - NODE_SIZE.width / 2, y: y - NODE_SIZE.height / 2 });

const getHandleCoords = (pos, width, height, handlePos) => {
    if (!pos) return [undefined, undefined];
    let y = pos.y + height / 2;
    let x = pos.x + width / 2;
    switch(handlePos) {
        case Position.Left: x = pos.x; break;
        case Position.Right: x = pos.x + width; break;
        case Position.Top: y = pos.y; break;
        case Position.Bottom: y = pos.y + height; break;

    }
    if (isNaN(x) || isNaN(y)) {
        console.warn("getHandleCoords calculated NaN position:", { pos, width, height, handlePos, x, y });
        return [undefined, undefined];
    }
    return [x, y];
};

const layoutEqual = (a, b) =>
    a?.positionAbsolute?.x === b?.positionAbsolute?.x &&
    a?.positionAbsolute?.y === b?.positionAbsolute?.y &&
    a?.width === b?.width &&
    a?.height === b?.height &&
    a?.sourcePosition === b?.sourcePosition &&
    a?.targetPosition === b?.targetPosition;

const LABEL_BOX_WIDTH = 86;
const LABEL_BOX_HEIGHT = 20;
// Borders (1px each side) plus horizontal padding (3px each side), as in
// EdgeLabel.module.css. A gated label's left border is 2px wider.
const LABEL_CHROME = 8;
const LABEL_GATE_BORDER_EXTRA = 2;
// Room each key icon takes out of the label text: the 10px icon plus its gap.
const LABEL_ICON_SLOT = 13;

/**
 * One choice's label on the canvas: its title, prefixed by an icon for each
 * thing it does with keys, bordered in its gate colour, and faded while a
 * different key is being inspected.
 */
const LabelBox = ({
    edgeItem,
    wrapperStyle,
    isOppositeDirection,
    textMeasureCtx,
    selectedEdgeIdsUser,
    selectedChoiceQueue,
    authUserId,
    labelStyles,
    onClick,
}) => {
    const keyHighlight = useKeyHighlight();

    const isAuthSelected = selectedEdgeIdsUser[authUserId] === edgeItem.id;
    const queuedUsers = selectedChoiceQueue[edgeItem.id];

    let isSelected = false;
    let fill = '#fff';
    let textColor = '#000';

    if (isAuthSelected) {
        isSelected = true;
        fill = hashStringToColor(authUserId);
        textColor = '#fff';
    } else if (queuedUsers && queuedUsers.length > 0) {
        isSelected = true;
        fill = hashStringToColor(queuedUsers[0]);
        textColor = '#fff';
    } else if (isOppositeDirection) {
        fill = '#e6e6e6';
    }

    const { grants, removes, gate } = edgeKeyRoles(edgeItem.data);
    const icons = [grants && 'grant', removes && 'remove', gate].filter(Boolean);
    const maxTextWidth =
        LABEL_BOX_WIDTH
        - LABEL_CHROME
        - (gate ? LABEL_GATE_BORDER_EXTRA : 0)
        - icons.length * LABEL_ICON_SLOT;
    const truncatedLabel = getEllipsizedText(edgeItem.data?.title || '', maxTextWidth, textMeasureCtx);

    const isDimmed = !!keyHighlight && !keyHighlight.edgeIds.has(String(edgeItem.id));

    const boxStyle = {
        width: `${LABEL_BOX_WIDTH}px`,
        height: `${LABEL_BOX_HEIGHT}px`,
        backgroundColor: fill,
        color: textColor,
    };
    if (gate) {
        boxStyle.borderLeftWidth = '3px';
        if (!isSelected) boxStyle.borderColor = KEY_VISUALS[gate].color;
    }

    const className = [
        labelStyles.edgeLabelWrapper,
        isSelected ? labelStyles.selected : '',
        isOppositeDirection ? labelStyles.opposite : '',
        isDimmed ? labelStyles.dimmed : '',
    ].filter(Boolean).join(' ');

    const iconTitles = {
        grant: 'Players pick up a key here',
        remove: 'Players lose a key here',
        has: 'Only players who have a key see this',
        lacks: 'Only players who do not have a key see this',
    };

    return (
        <div
            className={className}
            style={wrapperStyle}
            data-id={edgeItem.id}
            onClick={onClick}
            title={icons.length > 0 ? icons.map((role) => iconTitles[role]).join('\n') : undefined}
        >
            <div className={labelStyles.edgeLabelBox} style={boxStyle}>
                {icons.map((role) => (
                    <KeyRoleIcon
                        key={role}
                        role={role}
                        size={10}
                        className={labelStyles.labelIcon}
                        color={isSelected ? '#fff' : undefined}
                    />
                ))}
                <span className={labelStyles.labelText}>{truncatedLabel}</span>
            </div>
        </div>
    );
};

const EdgeLabelGroup = React.memo(({
    group,
    textMeasureCtx,
    selectedEdgeIdsUser,
    onSelectEdge,
    labelStyles,
    selectedChoiceQueue,
    authUserId,
}) => {
    const primaryEdge = group[0];
    const sourceId = primaryEdge?.source;
    const targetId = primaryEdge?.target;

    const sourceLayout = useStore(useCallback(s => {
        if (!sourceId) return null;
        const node = s.nodeInternals.get(sourceId);
        if (!node?.positionAbsolute) return null;
        return {
            positionAbsolute: node.positionAbsolute,
            width: node.width || 150,
            height: node.height || 50,
            sourcePosition: node.data?.sourcePosition || Position.Right,
            targetPosition: node.data?.targetPosition || Position.Left
        };
    }, [sourceId]), layoutEqual);

    const targetLayout = useStore(useCallback(s => {
        if (!targetId) return null;
        const node = s.nodeInternals.get(targetId);
        if (!node?.positionAbsolute) return null;
        return {
            positionAbsolute: node.positionAbsolute,
            width: node.width || 150,
            height: node.height || 50,
            sourcePosition: node.data?.sourcePosition || Position.Right,
            targetPosition: node.data?.targetPosition || Position.Left
        };
    }, [targetId]), layoutEqual);

    const labelPosition = useMemo(() => {
        if (!sourceLayout || !targetLayout || !primaryEdge) return null;

        const isLoop = primaryEdge.source === primaryEdge.target;

        const sourcePosition = sourceLayout.sourcePosition;
        const targetPosition = targetLayout.targetPosition;

        const [sourceEdgeX, sourceEdgeY] = getHandleCoords(sourceLayout.positionAbsolute, sourceLayout.width, sourceLayout.height, sourcePosition);
        const[targetEdgeX, targetEdgeY] = getHandleCoords(targetLayout.positionAbsolute, targetLayout.width, targetLayout.height, targetPosition);

        if (sourceEdgeX === undefined || sourceEdgeY === undefined ||
            targetEdgeX === undefined || targetEdgeY === undefined) {
            return null;
        }

        let labelX, labelY;

        if (isLoop) {
            const radiusY = 50;
            labelX = sourceLayout.positionAbsolute.x + sourceLayout.width / 2;
            labelY = sourceLayout.positionAbsolute.y - radiusY;
        } else {
            try {
                const[ , x, y ] = getSmoothStepPath({
                    sourceX: sourceEdgeX, sourceY: sourceEdgeY, sourcePosition,
                    targetX: targetEdgeX, targetY: targetEdgeY, targetPosition
                });
                labelX = x;
                labelY = y;
            } catch {
                return null;
            }
        }

        if (isNaN(labelX) || isNaN(labelY)) return null;

        return { labelX, labelY };
    }, [sourceLayout, targetLayout, primaryEdge]);

    const handleEdgeClick = useCallback((e) => {
        e.stopPropagation();
        const id = e.currentTarget.dataset.id;
        if (id) onSelectEdge(id);
    }, [onSelectEdge]);

    if (!labelPosition) return null;
    const { labelX, labelY } = labelPosition;

    const stackVerticalOffset = 25;

    const renderLabel = (edgeItem, wrapperStyle, isOppositeDirection = false) => (
        <LabelBox
            key={`label-${edgeItem.id}`}
            edgeItem={edgeItem}
            wrapperStyle={wrapperStyle}
            isOppositeDirection={isOppositeDirection}
            textMeasureCtx={textMeasureCtx}
            selectedEdgeIdsUser={selectedEdgeIdsUser}
            selectedChoiceQueue={selectedChoiceQueue}
            authUserId={authUserId}
            labelStyles={labelStyles}
            onClick={handleEdgeClick}
        />
    );

    if (group.length === 1) {
        return renderLabel(group[0], {
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all',
            zIndex: 4,
            cursor: 'pointer',
        });
    }

    const finalStackLabelY = labelY + stackVerticalOffset;
    return (
        <div
            style={{
                position: 'absolute',
                transform: `translate(-50%, -100%) translate(${labelX}px, ${finalStackLabelY}px)`,
                pointerEvents: 'all',
                zIndex: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
            className={labelStyles.edgeLabelStackWrapper}
        >
            {group.map((edgeItem, idx) => {
                const isOppositeDirection =
                    edgeItem.source === primaryEdge.target && edgeItem.target === primaryEdge.source;
                return renderLabel(
                    edgeItem,
                    {
                        cursor: 'pointer',
                        width: `${LABEL_BOX_WIDTH}px`,
                        height: `${LABEL_BOX_HEIGHT}px`,
                        marginBottom: idx < group.length - 1 ? '-1px' : '0',
                        pointerEvents: 'all',
                    },
                    isOppositeDirection
                );
            })}
        </div>
    );
});



const Canvas = ({ setRenderEditSidebar, isMobile }) => {
    const {
        nodes,
        edges,
        setGraph,
        scenario,
        loading,
        addSituation,
        addSituationWithChoice,
        addChoice,
        moveNodes,
        getNode,
    } = useCanvas();

    const {
        selectItem,
        clearSelection,
        selectNodes,
        selectedNodeId,
        selectedEdgeId,
        selectedNodeIds,
        selectedNodeIdsUser,
        selectedEdgeIdsUser,
        selectedNodeQueue,
        selectedChoiceQueue,
        edgeConnectingNodeId,
        setEdgeConnectingNodeId,
        reactFlowWrapperRef,
        highlightedKeyId,
    } = useEditorUI();

    const { userId } = useContext(AuthContext);
    const { project } = useReactFlow();
    const tutorialLocked = useTutorialLock('canvas');
    const { isActive: tutorialActive, isRunning: tutorialRunning, step: tutorialStep } = useTutorial();
    const reactFlowWrapper = useRef(null);
    const lastTapRef = useRef(0);
    const skipNextClickRef = useRef(null);

    /**
     * ReactFlow's automatic fit is a one-shot that runs the first time any node
     * reports its size. On a scenario that opened empty, that first node is the
     * one the author has just dropped, so the canvas would lurch and re-centre
     * underneath them. Deciding once, the moment the draft finishes loading,
     * keeps the fit for drafts that arrive with something on them and leaves a
     * new scenario sitting still at EMPTY_CANVAS_ZOOM.
     */
    const fitViewOnLoad = useRef(null);
    if (fitViewOnLoad.current === null && !loading) fitViewOnLoad.current = nodes.length > 0;

    /**
     * A tutorial places its situations at fixed fractions of the visible canvas,
     * which only means anything while that canvas stays put. The editor remounts
     * on the way back from every preview and re-centres itself on whatever is
     * already there, which would drop the remaining nodes on top of the ones the
     * tour had carefully spaced out. So for the length of a tour the viewport is
     * frozen — no auto-fit, no panning, no zooming — and the layout the steps
     * describe is the layout on screen. This asks `isRunning` rather than
     * `isActive` so it does not unfreeze for the instant a page is remounting.
     */
    const freezeViewport = tutorialRunning;

    // The "create at centre" gesture needs these bounds, and it lives in the
    // canvas context rather than here.
    useEffect(() => {
        if (reactFlowWrapperRef) reactFlowWrapperRef.current = reactFlowWrapper.current;
    }, [reactFlowWrapperRef]);

    /**
     * ReactFlow reports selection, dimension and in-flight drag changes as well
     * as finished moves. Only the last of those is a change to the draft, so the
     * rest are applied to local state and go no further.
     */
    const onNodesChange = useCallback(
        (changes) => {
            let nextNodes;
            setGraph((graph) => {
                nextNodes = applyNodeChanges(changes, graph.nodes);
                return { ...graph, nodes: nextNodes };
            });

            // `dragging: false` is the drag ENDING. A multi-select drag reports one
            // per node, and they all belong to the same gesture, so they become a
            // single op rather than one write per node.
            const settled = changes.filter((change) => change.dragging === false);
            if (settled.length === 0) return;

            const positions = settled
                .map((change) => {
                    const node = nextNodes?.find((n) => n.id === change.id);
                    return node?.position ? { id: node.id, position: node.position } : null;
                })
                .filter(Boolean);

            if (positions.length > 0) moveNodes(positions);
        },
        [setGraph, moveNodes]
    );

    const onEdgesChange = useCallback(
        (changes) => {
            setGraph((graph) => ({ ...graph, edges: applyEdgeChanges(changes, graph.edges) }));
        },
        [setGraph]
    );

    /**
     * Dragging a connection between two situations creates a choice. A connection
     * from a situation to itself is a hint — the op appliers derive that from the
     * shape on both sides, so nothing here has to say so.
     */
    const onConnect = useCallback(
        (params) => {
            const { source, target } = params;
            if (!source || !target) return;
            if (!getNode(source) || !getNode(target)) return;

            // A self-connection ends with a click on the same node; without this
            // the click would immediately reselect the node over the new choice.
            if (source === target) skipNextClickRef.current = source;

            addChoice({ source, target });

            if (!isMobile && typeof setRenderEditSidebar === 'function') {
                setRenderEditSidebar(true);
            }
        },
        [addChoice, getNode, isMobile, setRenderEditSidebar]
    );

    const onEdgeClick = useCallback(
        (event, edge) => {
            event.stopPropagation();
            selectItem('choice', edge.id);
        },
        [selectItem]
    );

    const onNodeClick = useCallback(
        (event, node) => {
            event.stopPropagation();
            if (event.target.closest('.react-flow__handle')) return;
            if (skipNextClickRef.current === node.id) {
                skipNextClickRef.current = null;
                return;
            }

            const { id } = node;
            const alreadySelected = selectedNodeId === id;

            if (isMobile) {
                // Mobile has no drag-to-connect, so a double tap arms the node and
                // the next tap on another node completes the connection.
                const now = Date.now();
                const isDoubleTap = now - lastTapRef.current < 300;

                if (isDoubleTap) {
                    event.preventDefault();
                    setEdgeConnectingNodeId((prev) => (prev === id ? null : id));
                    lastTapRef.current = 0;
                    return;
                }

                lastTapRef.current = now;

                if (edgeConnectingNodeId && edgeConnectingNodeId !== id) {
                    onConnect({ source: edgeConnectingNodeId, target: id });
                    setEdgeConnectingNodeId(null);
                } else if (edgeConnectingNodeId === id) {
                    setEdgeConnectingNodeId(null);
                } else if (alreadySelected) {
                    clearSelection();
                } else {
                    selectItem('situation', id);
                }
                return;
            }

            if (event.ctrlKey || event.metaKey) {
                selectNodes(
                    selectedNodeIds.includes(id)
                        ? selectedNodeIds.filter((nodeId) => nodeId !== id)
                        : [...selectedNodeIds, id]
                );
                return;
            }

            if (alreadySelected) clearSelection();
            else selectItem('situation', id);
        },
        [
            isMobile,
            edgeConnectingNodeId,
            setEdgeConnectingNodeId,
            onConnect,
            selectItem,
            clearSelection,
            selectNodes,
            selectedNodeId,
            selectedNodeIds,
        ]
    );

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();

            const wrapperBounds = reactFlowWrapper.current?.getBoundingClientRect();
            if (!wrapperBounds) return;

            const nodeType = event.dataTransfer.getData('application/reactflow');
            if (!nodeType) return;

            /**
             * A tutorial step may pin where its situation lands, because the tour
             * spotlights pairs of nodes and anchors a tooltip to one of them: a
             * node dropped on top of another, or underneath the tooltip, makes
             * the next instruction impossible to follow. `dropAt` is a fraction
             * of the visible canvas rather than a flow coordinate, so the layout
             * holds at any zoom, pan or window size.
             */
            const dropAt = tutorialActive ? tutorialStep?.dropAt : null;

            const position = dropAt
                ? topLeftOf(
                      project({
                          x: wrapperBounds.width * dropAt.x,
                          y: wrapperBounds.height * dropAt.y,
                      })
                  )
                : project({
                      x: event.clientX - wrapperBounds.left,
                      y: event.clientY - wrapperBounds.top,
                  });

            addSituation({
                position,
                data: {
                    // A new situation inherits the scenario's logging default; the
                    // author can turn it off per situation.
                    isLogged: !!scenario?.isLogged,
                    endpointType:
                        nodeType === 'startNode' ? 'start' : nodeType === 'endNode' ? 'end' : '',
                },
            });

            if (typeof setRenderEditSidebar === 'function') setRenderEditSidebar(true);
        },
        [addSituation, project, scenario, setRenderEditSidebar, tutorialActive, tutorialStep]
    );

    const nodeTypes = useMemo(
        () => ({
            situation: (nodeProps) => (
                <SituationNode
                    {...nodeProps}
                    edgeConnecting={edgeConnectingNodeId === nodeProps.id}
                    selectedUsers={selectedNodeQueue[nodeProps.id]}
                />
            ),
        }),
        [edgeConnectingNodeId, selectedNodeQueue]
    );

    const edgeTypes = useMemo(() => ({ choice: Edge }), []);

    // Edges between the same pair of nodes share a label position, so they are
    // rendered as one stack instead of overlapping.
    const edgeGroups = useMemo(() => {
        const groups = new Map();
        edges.forEach((edge) => {
            const key = [String(edge.source), String(edge.target)].sort().join('-');
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(edge);
        });
        groups.forEach((groupEdges) =>
            groupEdges.sort((a, b) => String(a.id).localeCompare(String(b.id)))
        );
        return Array.from(groups.values());
    }, [edges]);

    // What stays lit while a key is inspected in the Keys panel. `null` when
    // nothing is, which keeps the nodes, edges and labels from re-rendering.
    const keyHighlight = useMemo(() => {
        if (!highlightedKeyId) return null;
        return { keyId: highlightedKeyId, ...highlightSets({ nodes, edges }, highlightedKeyId) };
    }, [highlightedKeyId, nodes, edges]);

    const textMeasureCtx = useMemo(() => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (context) context.font = '12px Arial';
        return context;
    }, []);

    /**
     * Keyboard authoring: Cmd/Ctrl+Right grows the graph forward from the selected
     * situation, Cmd/Ctrl+Up adds a hint. Both are ordinary op batches now, so
     * they respond instantly instead of waiting on a transactional endpoint.
     */
    useEffect(() => {
        if (!selectedNodeId && !selectedEdgeId) return undefined;
        // A tutorial spells out every action it wants; a shortcut that silently
        // adds a situation would desynchronise it from what it is highlighting.
        if (tutorialLocked) return undefined;

        const isMac = navigator.platform.toUpperCase().includes('MAC');

        const handleKeyDown = throttle((event) => {
            const isModifierDown = isMac ? event.metaKey : event.ctrlKey;
            if (!isModifierDown) return;

            if (event.key === 'ArrowRight') {
                const sourceNode = selectedNodeId ? getNode(selectedNodeId) : null;
                if (!sourceNode) return;
                event.preventDefault();

                addSituationWithChoice({
                    source: selectedNodeId,
                    position: { x: sourceNode.position.x + 300, y: sourceNode.position.y },
                });
                return;
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();

                // From a situation: a hint on it. From a choice: another choice
                // between the same two situations.
                if (selectedNodeId) {
                    addChoice({ source: selectedNodeId, target: selectedNodeId });
                } else if (selectedEdgeId) {
                    const edge = edges.find((e) => e.id === selectedEdgeId);
                    if (edge) addChoice({ source: edge.source, target: edge.target });
                }
            }
        }, 500);

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedNodeId, selectedEdgeId, edges, getNode, addSituationWithChoice, addChoice, tutorialLocked]);

    return (
        <div
            onDrop={onDrop}
            onDragOver={(event) => event.preventDefault()}
            className={styles.container}
            ref={reactFlowWrapper}
            {...tutorialAttr(TUTORIAL_TARGETS.CANVAS)}
        >
            <KeyHighlightContext.Provider value={keyHighlight}>
            <ReactFlow
                nodes={nodes}
                onNodesChange={onNodesChange}
                edges={edges}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onEdgeClick={onEdgeClick}
                onPaneClick={clearSelection}
                onNodeClick={onNodeClick}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                fitView={!!fitViewOnLoad.current && !freezeViewport}
                fitViewOptions={FIT_VIEW_OPTIONS}
                defaultViewport={EMPTY_CANVAS_VIEWPORT}
                panOnDrag={!freezeViewport}
                zoomOnScroll={!freezeViewport}
                zoomOnPinch={!freezeViewport}
                zoomOnDoubleClick={!freezeViewport}
                snapToGrid
                deleteKeyCode={tutorialLocked ? null : ['Delete', 'Backspace']}
                aria-label="Scenario flow canvas"
            >
                <Controls />
                <Background color="#aaa" gap={16} />

                <EdgeLabelRenderer>
                    {edgeGroups.map((group) => {
                        if (group.length === 0) return null;
                        return (
                            <EdgeLabelGroup
                                key={`group-${group[0].id}`}
                                group={group}
                                textMeasureCtx={textMeasureCtx}
                                selectedEdgeIdsUser={selectedEdgeIdsUser}
                                onSelectEdge={(edgeId) => selectItem('choice', edgeId)}
                                labelStyles={labelStyles}
                                authUserId={userId}
                                selectedChoiceQueue={selectedChoiceQueue}
                            />
                        );
                    })}
                </EdgeLabelRenderer>
            </ReactFlow>
            </KeyHighlightContext.Provider>

            <ErrorOverlay />
        </div>
    );
};

export default Canvas;
